import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { StatusPill } from '../StatusPills';
import { DocumentViewer } from '../DocumentViewer';
import { LinkedOrganisations } from '../LinkedOrganisations';
import { OrgRequests, FollowUpBox } from './OrgRequests';
import { organisationsApi, documentsApi, mergeDocumentNames } from '../../lib/api';
import { formatDate, formatFileSize } from '../../lib/utils';

const SHOW_DOCS = 6;
const IN_ANALYSIS = new Set(['queued', 'analysing']);

const Tile = ({ label, value, hint, tone, busy, testId }) => (
  <div className={`rounded-lg p-5 flex flex-col gap-1 ${tone === 'needs' ? 'bg-amber-wash' : 'bg-ground'}`} data-testid={testId}>
    <span className={`pd-eyebrow ${tone === 'needs' ? 'text-amber-ink' : ''}`}>{label}</span>
    <span className={`font-heading text-[42px] font-bold tracking-[-0.035em] leading-none ${tone === 'needs' ? 'text-amber-ink' : 'text-ink'}`}>{value}</span>
    {hint && (
      <span className={`text-sm flex items-center gap-1.5 ${tone === 'needs' ? 'text-amber-ink' : 'text-ink-55'}`}>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden="true" />}{hint}
      </span>
    )}
  </div>
);

// Records per year — one series, so no legend; each bar names its year and
// count on hover, and the same numbers are listed for screen readers.
const RecordsByYear = ({ years }) => {
  const rows = [...(years || [])].filter((y) => y.year).sort((a, b) => a.year - b.year);
  if (rows.length < 2) return null;
  const all = [];
  for (let y = rows[0].year; y <= rows[rows.length - 1].year; y += 1) all.push({ year: y, count: rows.find((r) => r.year === y)?.count || 0 });
  const max = Math.max(...all.map((r) => r.count));
  const peak = all.find((r) => r.count === max);
  return (
    <div className="rounded-lg bg-ground p-5 space-y-3" data-testid="org-records-by-year">
      <p className="pd-eyebrow" id="org-years-title">Records by year</p>
      <div className="flex items-end gap-[2px] h-[130px]" aria-hidden="true">
        {all.map((r) => (
          <div key={r.year} className="group relative flex-1 h-full flex items-end" title={`${r.year}: ${r.count} record${r.count === 1 ? '' : 's'}`}>
            <div className="w-full rounded-t-[4px] bg-shape group-hover:bg-ink-55 transition-colors duration-state" style={{ height: r.count ? `${Math.max(3, (r.count / max) * 100)}%` : '0' }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between pd-mono" aria-hidden="true"><span>{all[0].year}</span><span>{all[all.length - 1].year}</span></div>
      <p className="text-sm text-ink-55">Most was written in {peak.year} ({peak.count} record{peak.count === 1 ? '' : 's'}).</p>
      <table className="sr-only" aria-labelledby="org-years-title">
        <thead><tr><th>Year</th><th>Records</th></tr></thead>
        <tbody>{all.map((r) => <tr key={r.year}><td>{r.year}</td><td>{r.count}</td></tr>)}</tbody>
      </table>
    </div>
  );
};

const DeleteOrganisation = ({ org }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [withdraw, setWithdraw] = useState(null);
  const [busy, setBusy] = useState(false);
  const docs = org.document_count || 0;
  const remove = async () => {
    setBusy(true);
    try {
      await organisationsApi.delete(org.id, { confirmActiveSar: !!withdraw });
      toast.success(withdraw ? 'Organisation deleted and the request withdrawn.' : 'Organisation deleted.');
      navigate('/organisations');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.requires_confirmation) {
        setWithdraw({ count: detail.in_progress_sar_count || 1 });
      } else {
        toast.error((typeof detail === 'string' && detail) || 'The organisation could not be deleted.');
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant="ghost" size="sm" className="text-breach-ink hover:text-breach-ink" onClick={() => setOpen(true)} data-testid="delete-org-detail-btn">Remove this organisation</Button>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setWithdraw(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">Delete {org.name}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-ink-70">
              <span className="block">This permanently removes the organisation and all its details, every request you sent them{docs ? `, and ${docs} document${docs === 1 ? '' : 's'} from them` : ''}. It can’t be undone.</span>
              {docs > 0 && <span className="block text-sm text-amber-ink font-medium">Download the documents first if you want to keep them.</span>}
              {withdraw && (
                <span className="block p-3 bg-breach-wash rounded-lg text-sm text-breach-ink font-medium">
                  {withdraw.count === 1 ? 'A request to them is still in progress.' : `${withdraw.count} requests to them are still in progress.`}
                  {' '}Deleting anyway will email {org.name} to say {withdraw.count === 1 ? 'it is' : 'they are'} withdrawn, then delete everything.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(); }} disabled={busy} className="bg-breach-wash text-breach hover:bg-[#F6D2CA]" data-testid="confirm-delete-org-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}{withdraw ? 'Withdraw and delete' : 'Delete organisation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * "Requests & data" — everything about the file rather than the time: your
 * requests (and asking for what's missing), what they sent and what's
 * blacked out, the documents, the details your letters use, linked
 * organisations, and removing the organisation.
 */
export const OrgData = ({ org, sars, followUpGaps, cabinet, unanalysedCount = 0, onChanged, onUpload, onAnalyse }) => {
  const [docs, setDocs] = useState(org.documents || []);
  const [showAll, setShowAll] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [zipping, setZipping] = useState(false);
  const totals = cabinet?.totals || {};

  // Filenames are sealed under the user's key: decrypt them after render.
  useEffect(() => {
    const list = org.documents || [];
    setDocs(list);
    if (!list.length) return;
    let live = true;
    documentsApi.names(list.map((d) => d.id)).then((names) => { if (live) setDocs(mergeDocumentNames(list, names)); }).catch(() => {});
    return () => { live = false; };
  }, [org.documents]);

  const fromThem = docs.filter((d) => d.source !== 'user_upload').length;
  const analysing = docs.filter((d) => IN_ANALYSIS.has(d.analysis_status)).length;
  const docCount = Math.max(org.document_count || 0, docs.length);
  const hasSent = docCount > 0;
  const recordsHint = analysing
    ? `Analysing ${analysing} document${analysing === 1 ? '' : 's'}…`
    : totals.multiple_copies ? `${totals.multiple_copies} appear in more than one copy`
      : totals.records ? null : unanalysedCount ? 'Not analysed yet' : 'Nothing found yet';
  const yours = docs.length - fromThem;
  const downloadAll = async () => {
    setZipping(true);
    try {
      const response = await organisationsApi.downloadDocumentsZip(org.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${org.name.replace(/\s+/g, '_')}_documents.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'The documents could not be downloaded.');
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="org-data">
      {hasSent && (
        <section aria-labelledby="org-sent-heading" className="space-y-4" data-testid="org-sent">
          <h2 id="org-sent-heading" className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">What they sent</h2>
          <div className={`grid grid-cols-2 ${totals.challenge_recommended > 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
            <Tile label="Documents" value={docCount} hint={[fromThem ? `${fromThem} from them` : null, yours ? `${yours} you uploaded` : null].filter(Boolean).join(', ')} testId="org-tile-documents" />
            <Tile label="Records found" value={totals.records ?? 0} hint={recordsHint} busy={analysing > 0} testId="org-tile-records" />
            <Tile label="Blacked out" value={totals.redactions ?? 0} hint={totals.redactions ? (totals.redactions_recovered ? `${totals.redactions_recovered} recovered from other copies` : 'None recovered yet') : 'Nothing blacked out'} testId="org-tile-redactions" />
            {totals.challenge_recommended > 0 && (
              <Tile label="Worth challenging" value={totals.challenge_recommended} hint="blacked-out passages that look wider than needed" tone="needs" testId="org-tile-challenge" />
            )}
          </div>
          {unanalysedCount > 0 && (
            <div className="rounded-lg bg-amber-wash px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="org-analyse-prompt-data">
              <p className="text-[15px] text-amber-ink">{unanalysedCount} document{unanalysedCount === 1 ? '' : 's'} waiting to be analysed.</p>
              <Button size="sm" variant="secondary" className="bg-surface hover:bg-inset" onClick={onAnalyse}>Analyse {unanalysedCount === 1 ? 'it' : 'them'}</Button>
            </div>
          )}
          <RecordsByYear years={totals.years} />
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        <div className="flex flex-col gap-4 min-w-0">
          <h2 className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">Your requests to them</h2>
          <OrgRequests
            org={org} sars={sars} gaps={followUpGaps} hasOwnRecords={hasSent} onChanged={onChanged}
            showFollowUp={false} className={hasSent && sars.length > 0 && !org.unresolved ? '' : 'flex-1'}
          />
          {hasSent && sars.length > 0 && !org.unresolved && (
            <FollowUpBox org={org} sars={sars} gaps={followUpGaps} hasOwnRecords={hasSent} onChanged={onChanged} className="flex-1" />
          )}
        </div>

        <section aria-labelledby="org-docs-heading" className="flex flex-col gap-4 min-w-0" data-testid="org-documents">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 id="org-docs-heading" className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">Documents</h2>
            {hasSent && (
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={onUpload} data-testid="org-upload-doc">Upload</Button>
                <Button size="sm" variant="ghost" onClick={downloadAll} disabled={zipping} data-testid="download-all-zip-btn">
                  {zipping ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Preparing download" /> : 'Download all'}
                </Button>
              </div>
            )}
          </div>
          {!hasSent ? (
            <div className="flex-1 flex flex-col justify-center rounded-lg bg-ground py-10 px-6 text-center" data-testid="org-no-documents">
              <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mx-auto mb-4" />
              <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink mb-1">No documents yet</p>
              <p className="text-base text-ink-55 max-w-md mx-auto mb-5">
                Upload anything you already have from {org.name}{org.unresolved ? '.' : ', or send them a request and what they send lands here.'}
              </p>
              <div><Button variant="secondary" onClick={onUpload} data-testid="org-upload-doc">Upload a document</Button></div>
            </div>
          ) : (
            <div className="flex-1 rounded-lg bg-ground p-3 space-y-1.5">
              <ul className="space-y-1.5">
                {(showAll ? docs : docs.slice(0, SHOW_DOCS)).map((doc) => {
                  const theirs = doc.source !== 'user_upload';
                  return (
                    <li key={doc.id}>
                      <button type="button" onClick={() => setViewing(doc)} className="w-full text-left rounded-md bg-surface hover:bg-inset transition-colors duration-state px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`doc-row-${doc.id}`}>
                        <span className="min-w-0">
                          <span className="block text-[15px] font-medium text-ink truncate">{doc.original_filename || 'Document'}</span>
                          <span className="block text-[13px] text-ink-55">{[doc.file_size ? formatFileSize(doc.file_size) : null, formatDate(doc.uploaded_at || doc.created_at)].filter(Boolean).join(' · ')}</span>
                        </span>
                        <StatusPill tone={theirs ? 'done' : 'neutral'} size="sm" className="shrink-0">{theirs ? 'Sent by them' : 'Your upload'}</StatusPill>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {docs.length > SHOW_DOCS && (
                <button type="button" onClick={() => setShowAll((v) => !v)} className="px-1 text-sm font-semibold text-brand hover:text-brand-ink">{showAll ? 'Show fewer' : `Show all ${docs.length}`}</button>
              )}
            </div>
          )}
        </section>
      </div>

      <LinkedOrganisations orgId={org.id} />

      <div className="border-t border-rule pt-5">
        <DeleteOrganisation org={org} />
      </div>

      <DocumentViewer doc={viewing} open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }} />
    </div>
  );
};

export default OrgData;
